// The ⋯ menu (design §4). Pipeline entries (manual override, cancel, late
// fee, sign) come from context; surface-specific entries (edit, collect,
// duplicate, delete, remind) are passed in and rendered only when given.
import React, { useEffect, useRef, useState } from 'react';
import axios from '../../lib/axiosClient';
import { useOrderActions } from './OrderActionContext';

const item = (color = '#e2e8f0', extra = {}) => ({ padding: '9px 12px', fontSize: 12, color, cursor: 'pointer', whiteSpace: 'nowrap', ...extra });

export default function OrderMoreMenu({ order, actions: a = {}, align = 'right', size = 'sm' }) {
  const ctx = useOrderActions();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  if (!order) return null;

  const overdue = !!order.next_action?.is_overdue;
  const cancel = order.cancel_action;
  const go = (fn) => (e) => { e.stopPropagation(); setOpen(false); fn && fn(order); };
  const remind = async (o) => {
    if (!window.confirm('Send overdue reminder to ' + o.client_name + '?')) return;
    try { const r = await axios.post('/api/rentals/orders/' + o.id + '/send-overdue-reminder'); ctx.showFlash?.('Reminder sent · SMS ' + (r.data.smsSent ? '✓' : '—') + ' · Email ' + (r.data.emailSent ? '✓' : '—')); }
    catch (err) { ctx.showFlash?.(err.response?.data?.error || 'Failed', 'error'); }
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      <button type="button" title="More actions" aria-label="More actions" data-testid="order-more" data-order-id={order.id}
        onClick={() => setOpen(o => !o)}
        style={{ padding: size === 'sm' ? '5px 10px' : '8px 12px', background: '#252840', color: '#e2e8f0', border: 'none', borderRadius: 6, fontSize: size === 'sm' ? 12 : 14, fontWeight: 700, cursor: 'pointer' }}>⋯</button>
      {open && (
        <div role="menu" style={{ position: 'absolute', [align]: 0, top: '100%', marginTop: 4, background: '#1e2235', border: '1px solid #333', borderRadius: 8, zIndex: 3000, minWidth: 200, boxShadow: '0 6px 18px rgba(0,0,0,0.45)', padding: '4px 0' }}>
          {a.edit      && <div style={item()} onClick={go(a.edit)}>✏️ Edit</div>}
          {a.collect   && <div style={item('#4ade80')} onClick={go(a.collect)}>💰 Collect payment</div>}
          {order.payment?.can_refund && <div data-testid="menu-refund" data-highlight={order.payment.refund_reason || ''} style={item(order.payment.refund_reason ? '#a78bfa' : '#e2e8f0')} onClick={go(ctx.openRefund)}>↩ Refund…{order.payment.refund_reason === 'overpaid' ? ` (owed $${Number(order.payment.owed_to_client).toFixed(2)})` : order.payment.refund_reason === 'cancelled' ? ' (cancelled with money)' : ''}</div>}
          {order.payment?.can_refund && <div style={item('#94a3b8')} onClick={go(ctx.openLedger)}>📒 Ledger</div>}
          {!order.signed_at
            ? <div style={item('#fb923c')} onClick={go(ctx.openSign)}>✍️ Get signature</div>
            : <div style={item('#4ade80', { cursor: 'default' })} title={'Signed ' + new Date(order.signed_at).toLocaleDateString()}>✓ Signed</div>}
          {overdue && <div style={item('#f97316')} onClick={go(remind)}>📨 Send overdue reminder</div>}
          {overdue && <div style={item('#fbbf24')} onClick={go(ctx.openLateFee)}>💰 Add late fee manually</div>}
          {a.duplicate && <div style={item('#a78bfa')} onClick={go(a.duplicate)}>⎘ Duplicate</div>}
          <div style={item('#e2e8f0', { borderTop: '1px solid #2a2d3e', marginTop: 4, paddingTop: 10 })} onClick={go(ctx.openManual)}>🔧 Change status manually…</div>
          {cancel?.enabled
            ? <div style={item('#f87171')} onClick={go((o) => ctx.openAction(o, 'cancel'))}>✕ {cancel.label}</div>
            : (cancel && ['started'].includes(order.status) && <div style={item('#64748b', { cursor: 'not-allowed' })} title={cancel.reason}>✕ Cancel order — {cancel.reason}</div>)}
          {a.delete    && <div style={item('#f87171', { borderTop: '1px solid #2a2d3e', marginTop: 4, paddingTop: 10 })} onClick={go(a.delete)}>🗑️ Delete</div>}
        </div>
      )}
    </div>
  );
}
